import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { groupBy } from "lodash";
import moment from "moment-timezone";
import dayjsTZ from "../../../../utils/dayjs";

// excel sheet names must be unique, non-empty, <= 31 chars and free of : \ / ? * [ ].
// violating any of these produces a workbook Excel can only "recover" into one sheet.
const makeSafeSheetNamer = () => {
  const used = new Set();
  return (name) => {
    let clean =
      String(name ?? "")
        .replace(/[:\\/?*[\]]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 31) || "Sheet";
    let candidate = clean;
    let i = 1;
    while (used.has(candidate)) {
      const suffix = `_${i++}`;
      candidate = clean.slice(0, 31 - suffix.length) + suffix;
    }
    used.add(candidate);
    return candidate;
  };
};

// format a picker value directly (preserving the calendar day the user picked).
// re-wrapping through dayjsTZ would re-convert the instant under a per-date DST
// offset and shift the day across midnight (e.g. 1 March -> 28/29 February).
const formatDay = (v) =>
  v && typeof v.format === "function"
    ? v.format("DD/MM/YYYY")
    : v
      ? dayjsTZ(v).format("DD/MM/YYYY")
      : "";

export const exportToCSV = (apiData, fileName, time, note) => {
  if (!Array.isArray(apiData) || apiData.length === 0) return;
  let dateRange =
    time?.[0] && time?.[1]
      ? `${formatDay(time[0])} - ${formatDay(time[1])}`
      : "";
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";
  const safeSheetName = makeSafeSheetNamer();
  // one sheet per employee. the combined-name column ("Punonjësi") isn't always
  // present — some report configs use separate first/last name columns — so fall
  // back to "Emri" + "Mbiemri" before lumping rows into a single group.
  const groupKeyForRow = (row) => {
    const full = row?.["Punonjësi"];
    if (full != null && String(full).trim() !== "") return String(full).trim();
    const first = String(row?.["Emri"] ?? "").trim();
    const last = String(row?.["Mbiemri"] ?? "").trim();
    return `${first} ${last}`.trim() || "Të tjera";
  };
  const grouped = groupBy(apiData, groupKeyForRow);

  // only the selected/exported columns — never inject extra ones.
  const columns = Object.keys(apiData[0]);
  const firstColumn = columns[0];
  // numeric columns are the only ones that get a summed total.
  const numericColumns = columns.filter((col) =>
    apiData.some((row) => typeof row[col] === "number"),
  );

  let wbtest = { Sheets: {}, SheetNames: [] };

  Object.entries(grouped).forEach(([key, value]) => {
    const rows = [...value];

    // totals row restricted to existing numeric columns.
    const totalsRow = {};
    numericColumns.forEach((col) => {
      totalsRow[col] = parseFloat(
        value
          .reduce(
            (sum, r) =>
              sum + (typeof r[col] === "number" ? r[col] : Number(r[col]) || 0),
            0,
          )
          .toFixed(2),
      );
    });
    rows.push(totalsRow);

    if (note !== "" && !!note) {
      rows.push({}); // blank spacer row
      rows.push({ [firstColumn]: "Shenimi" });
      rows.push({ [firstColumn]: note.toString() });
    }

    const sheetName = safeSheetName(key);
    wbtest.SheetNames.push(sheetName);
    // `header: columns` pins the column set/order to exactly the selected columns.
    wbtest.Sheets[sheetName] = XLSX.utils.json_to_sheet(rows, {
      header: columns,
    });
    wbtest.Sheets[sheetName]["!cols"] = columns.map(() => ({ wch: 15 }));
  });
  const excelBuffer = XLSX.write(wbtest, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(data, fileName + dateRange + fileExtension);
};
